import type { CSSProperties } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

import { useProjectStatusEnabled } from "./projectStatusAvailability";
import { PROJECT_STATUS_COLOURS, type ProjectStatus } from "./projectStatus";

interface ProjectStatusPillProps {
  status: ProjectStatus;
  /** Omit when the user may not change the status (MANAGE_PROJECT_STATUS):
   *  the pill still expands on hover but is not a control. */
  onSetStatus?: (status: ProjectStatus) => void;
  /** A change is in flight — the menu's options are disabled meanwhile. */
  busy?: boolean;
  className?: string;
}

const STATUSES: ProjectStatus[] = ["active", "inactive"];

/**
 * A project's status as a bare dot that widens into the labelled pill on hover
 * or keyboard focus (and stays open while its menu is). With `onSetStatus` it
 * is a button opening an Active / Inactive menu — a menu, not a click-to-flip
 * toggle, so a stray click can't change a project's status.
 *
 * It is interactive, so it must never sit inside another button: the dashboard
 * tile places it beside its ⋮ menu, over the tile.
 */
export function ProjectStatusPill({
  status,
  onSetStatus,
  busy,
  className,
}: ProjectStatusPillProps) {
  const { t } = useLanguage();
  const enabled = useProjectStatusEnabled();
  if (!enabled) return null;

  const label = statusLabel(t.projects.status, status);
  const colours = PROJECT_STATUS_COLOURS[status];
  const pillClass = cn(
    // `group/pill` drives the label's width; `data-[state=open]` is the menu
    // trigger's own attribute, so an open menu holds the pill open.
    "group/pill inline-flex h-5 shrink-0 items-center rounded-full px-1.5 text-[11px] font-medium",
    "transition-colors duration-200 motion-reduce:transition-none",
    "hover:bg-[var(--pill-bg)] focus-visible:bg-[var(--pill-bg)] data-[state=open]:bg-[var(--pill-bg)]",
    className
  );
  const pillStyle = { "--pill-bg": colours.bgColor, color: colours.textColor } as CSSProperties;
  const pillContent = (
    <>
      <span
        aria-hidden="true"
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: colours.dotColor }}
      />
      <span
        className={cn(
          "max-w-0 overflow-hidden whitespace-nowrap opacity-0",
          "transition-[max-width,opacity,margin] duration-200 ease-out motion-reduce:transition-none",
          "group-hover/pill:ml-1 group-hover/pill:max-w-[8rem] group-hover/pill:opacity-100",
          "group-focus-visible/pill:ml-1 group-focus-visible/pill:max-w-[8rem] group-focus-visible/pill:opacity-100",
          "group-data-[state=open]/pill:ml-1 group-data-[state=open]/pill:max-w-[8rem] group-data-[state=open]/pill:opacity-100"
        )}
      >
        {label}
      </span>
    </>
  );

  if (!onSetStatus) {
    return (
      <span role="img" aria-label={label} title={label} className={pillClass} style={pillStyle}>
        {pillContent}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${t.projects.status.filterLabel}: ${label}`}
          className={cn(
            pillClass,
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
          style={pillStyle}
        >
          {pillContent}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={status}
          onValueChange={(value) => {
            const next = STATUSES.find((option) => option === value);
            if (next && next !== status && !busy) onSetStatus(next);
          }}
        >
          {STATUSES.map((option) => (
            // The primitive, not the shared DropdownMenuRadioItem: its leading
            // radio dot would sit beside the status dot, two dots per row.
            <DropdownMenuPrimitive.RadioItem
              key={option}
              value={option}
              disabled={busy}
              className="relative flex cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: PROJECT_STATUS_COLOURS[option].dotColor }}
              />
              {statusLabel(t.projects.status, option)}
              <DropdownMenuPrimitive.ItemIndicator className="absolute right-2 flex items-center">
                <Check className="h-4 w-4" />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function statusLabel(copy: { active: string; inactive: string }, status: ProjectStatus): string {
  return status === "inactive" ? copy.inactive : copy.active;
}
